/**
 * WriteProposalCard — inline preview card shown in the chat flow when
 * a write operation (create test case, update steps, update variables)
 * needs user review before saving. Renders a structured preview with
 * Save / Discard buttons.
 *
 * Dispatches to sub-views based on proposal_data.type:
 *   - TestCasePreview: name, URL, numbered steps, variables, tags
 *   - StepDiffPreview: current vs proposed with color-coded changes
 *   - VariablePreview: table with added/updated/unchanged
 */

import { useState } from 'react'
import { Save, X, FileText, Variable, Plus, Pencil, Minus, AlertTriangle, Globe, Check, ChevronDown, ChevronRight, FolderOpen } from 'lucide-react'
import type { PendingAction, TestCaseProposal, TestCaseUpdateProposal, StepUpdateProposal, EnvironmentVariableProposal } from '../types'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WriteProposalCardProps {
  pendingAction: PendingAction
  onSave: (nonce: string) => void
  onDiscard: () => void
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function TestCasePreview({ proposal }: { proposal: TestCaseProposal }) {
  const [showKnowledgeHub, setShowKnowledgeHub] = useState(false)
  const duplicates = proposal.similar_test_cases
  const coverage = proposal.variable_coverage
  const khPages = proposal.knowledge_hub_context
  const envs = proposal.available_environments

  return (
    <div className="space-y-3">
      {/* Duplicate warning — shown first as a banner */}
      {duplicates && duplicates.length > 0 && (
        <div className="rounded-lg bg-warning-soft border border-warning/30 p-2.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-warning-foreground mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Similar test cases found ({duplicates.length})
          </div>
          <ul className="space-y-1">
            {duplicates.map((tc) => (
              <li key={tc.id} className="flex items-center gap-2 text-xs text-warning-foreground">
                <span className="font-medium">{tc.name}</span>
                <span className="rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] text-warning-foreground">
                  {tc.match_reason}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground">{proposal.name}</div>
        {proposal.description && (
          <p className="text-xs text-muted-foreground">{proposal.description}</p>
        )}
        <div className="text-xs text-subtle-foreground">{proposal.target_url}</div>
        {/* Project assignment */}
        <div className="flex items-center gap-1.5 mt-1">
          <FolderOpen className="w-3 h-3 text-subtle-foreground" />
          {proposal.project_name ? (
            <span className="text-xs text-muted-foreground">
              Project: <span className="font-medium">{proposal.project_name}</span>
            </span>
          ) : (
            <span className="text-xs text-subtle-foreground italic">No project assigned</span>
          )}
        </div>
      </div>

      {/* Available environments */}
      {envs && envs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="text-subtle-foreground">Environments:</span>
          {envs.map((env) => (
            <span
              key={env.id}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                env.is_default
                  ? 'bg-info-soft text-info-foreground'
                  : 'bg-foreground/[0.06] text-muted-foreground'
              }`}
            >
              {env.name}
              {env.is_default && ' (default)'}
              {env.base_url && <span className="text-subtle-foreground ml-0.5">· {env.base_url}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Steps */}
      <div>
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
          Steps ({proposal.steps.length})
        </div>
        <ol className="space-y-1">
          {proposal.steps.map((step, i) => (
            <li key={i} className="flex gap-2 text-xs">
              <span className="text-subtle-foreground font-mono w-5 shrink-0 text-right">{step.step_number}.</span>
              <div className="min-w-0">
                <span className="text-foreground">{step.instruction}</span>
                {step.expected_result && (
                  <span className="text-subtle-foreground ml-1">→ {step.expected_result}</span>
                )}
                {step.step_type !== 'action' && (
                  <span className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium bg-accent-soft text-accent">
                    {step.step_type}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Variable coverage */}
      {coverage && Object.keys(coverage).length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Variable className="w-3 h-3" />
            Variable Coverage
          </div>
          <div className="space-y-1">
            {Object.entries(coverage).map(([varName, info]) => (
              <div key={varName} className="flex items-center gap-2 text-xs">
                <code className="font-mono text-foreground">{`{{${varName}}}`}</code>
                {info.is_new ? (
                  <span className="rounded-full bg-warning-soft text-warning-foreground px-1.5 py-0.5 text-[10px] font-medium">
                    New — not in any environment
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success-soft text-success-foreground px-1.5 py-0.5 text-[10px] font-medium">
                    <Check className="w-2.5 h-2.5" />
                    {info.defined_in.map(e => e.env_name).join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variables & Tags (simple list, kept for when no coverage data) */}
      {!coverage && proposal.variables && Object.keys(proposal.variables).length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Variable className="w-3 h-3" />
            <span>{Object.keys(proposal.variables).join(', ')}</span>
          </div>
        </div>
      )}
      {proposal.tags && proposal.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 text-xs">
          {proposal.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] text-muted-foreground">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Knowledge Hub context — collapsible */}
      {khPages && khPages.length > 0 && (
        <div>
          <button
            onClick={() => setShowKnowledgeHub(!showKnowledgeHub)}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
          >
            {showKnowledgeHub ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <Globe className="w-3 h-3" />
            Knowledge Hub — {khPages.length} matching page{khPages.length > 1 ? 's' : ''}
          </button>
          {showKnowledgeHub && (
            <div className="mt-1.5 space-y-2 pl-5">
              {khPages.map((page, i) => (
                <div key={i} className="text-xs">
                  <div className="font-medium text-foreground">
                    {page.page_title || page.url}
                  </div>
                  <div className="text-subtle-foreground text-[10px] truncate">{page.url}</div>
                  {page.buttons && page.buttons.length > 0 && (
                    <div className="text-muted-foreground mt-0.5">
                      <span className="text-subtle-foreground">Buttons:</span> {page.buttons.join(', ')}
                    </div>
                  )}
                  {page.form_fields && page.form_fields.length > 0 && (
                    <div className="text-muted-foreground mt-0.5">
                      <span className="text-subtle-foreground">Fields:</span>{' '}
                      {page.form_fields.map(f => f.label || f.name).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function TestCaseUpdatePreview({ proposal }: { proposal: TestCaseUpdateProposal }) {
  const updates = proposal.updates || {}
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <Pencil className="w-4 h-4 text-accent" />
        <span className="font-medium text-foreground">{proposal.test_case_name}</span>
      </div>
      <div className="space-y-1.5">
        {Object.entries(updates).map(([field, value]) => (
          <div key={field} className="flex items-start gap-2 px-2.5 py-1.5 bg-warning-soft border border-warning/30 rounded-md">
            <Pencil className="w-3 h-3 text-warning-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <span className="text-[10px] font-semibold uppercase text-warning-foreground">{field}</span>
              <div className="text-foreground text-xs mt-0.5">
                {Array.isArray(value) ? value.join(', ') : String(value || '(empty)')}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

type Step = StepUpdateProposal['current_steps'][number]

type DiffRow =
  | { kind: 'modified'; current: Step; proposed: Step }
  | { kind: 'added'; proposed: Step }
  | { kind: 'removed'; current: Step }

type UnifiedRow =
  | { kind: 'unchanged'; proposed: Step }
  | { kind: 'modified'; current: Step; proposed: Step }
  | { kind: 'added'; proposed: Step }
  | { kind: 'removed'; current: Step }

// LCS by (instruction, step_type) to align unchanged steps. Anything not in
// the LCS is a remove or add; consecutive remove+add pairs collapse into
// "modified" so an in-place edit shows as one row instead of two. This
// avoids the cascading-modified noise a naive positional diff would produce
// on a mid-list insert (every step after the insertion point would falsely
// look modified).
//
// Returns both `rows` (focused diff — only changes, used for the summary
// section) and `unifiedRows` (full sequence including unchanged, used for
// the inline-annotated "Final test case" view).
function diffSteps(
  current: Step[],
  proposed: Step[],
): { rows: DiffRow[]; unifiedRows: UnifiedRow[]; unchangedCount: number } {
  const eq = (a: Step, b: Step) => a.instruction === b.instruction && a.step_type === b.step_type
  const n = current.length
  const m = proposed.length
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = eq(current[i], proposed[j])
        ? 1 + dp[i + 1][j + 1]
        : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  type Op =
    | { kind: 'same'; proposed: Step }
    | { kind: 'add'; proposed: Step }
    | { kind: 'remove'; current: Step }
  const ops: Op[] = []
  let i = 0
  let j = 0
  while (i < n || j < m) {
    if (i < n && j < m && eq(current[i], proposed[j])) {
      ops.push({ kind: 'same', proposed: proposed[j] })
      i++
      j++
    } else if (j < m && (i >= n || dp[i][j + 1] >= dp[i + 1][j])) {
      ops.push({ kind: 'add', proposed: proposed[j] })
      j++
    } else {
      ops.push({ kind: 'remove', current: current[i] })
      i++
    }
  }

  const rows: DiffRow[] = []
  const unifiedRows: UnifiedRow[] = []
  let unchanged = 0
  let k = 0
  while (k < ops.length) {
    const op = ops[k]
    if (op.kind === 'same') {
      unchanged++
      unifiedRows.push({ kind: 'unchanged', proposed: op.proposed })
      k++
      continue
    }
    const removes: Step[] = []
    const adds: Step[] = []
    while (k < ops.length && ops[k].kind !== 'same') {
      const inner = ops[k]
      if (inner.kind === 'remove') removes.push(inner.current)
      else if (inner.kind === 'add') adds.push(inner.proposed)
      k++
    }
    const pairs = Math.min(removes.length, adds.length)
    for (let p = 0; p < pairs; p++) {
      rows.push({ kind: 'modified', current: removes[p], proposed: adds[p] })
      unifiedRows.push({ kind: 'modified', current: removes[p], proposed: adds[p] })
    }
    for (let p = pairs; p < removes.length; p++) {
      rows.push({ kind: 'removed', current: removes[p] })
      unifiedRows.push({ kind: 'removed', current: removes[p] })
    }
    for (let p = pairs; p < adds.length; p++) {
      rows.push({ kind: 'added', proposed: adds[p] })
      unifiedRows.push({ kind: 'added', proposed: adds[p] })
    }
  }

  return { rows, unifiedRows, unchangedCount: unchanged }
}

function StepDiffPreview({ proposal }: { proposal: StepUpdateProposal }) {
  const { rows, unifiedRows, unchangedCount } = diffSteps(
    proposal.current_steps,
    proposal.proposed_steps,
  )

  return (
    <div className="space-y-3 text-xs">
      <div className="text-sm font-semibold text-foreground">{proposal.test_case_name}</div>
      <div className="text-muted-foreground">{proposal.changes_summary}</div>

      {rows.length === 0 ? (
        <div className="text-subtle-foreground italic py-1">No step changes detected.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((row, i) => {
            if (row.kind === 'modified') {
              return (
                <div key={i} className="rounded bg-warning-soft border border-warning/30 px-2 py-1.5">
                  <div className="text-[10px] font-mono text-warning-foreground uppercase tracking-wide mb-0.5">
                    Step {row.proposed.step_number} · modified
                  </div>
                  <div className="text-destructive-foreground line-through">{row.current.instruction}</div>
                  <div className="text-success-foreground">→ {row.proposed.instruction}</div>
                </div>
              )
            }
            if (row.kind === 'added') {
              return (
                <div key={i} className="rounded bg-success-soft border border-success/30 px-2 py-1 text-success-foreground">
                  <span className="font-mono mr-1">+ Step {row.proposed.step_number}</span>
                  {row.proposed.instruction}
                </div>
              )
            }
            return (
              <div key={i} className="rounded bg-destructive-soft border border-destructive/30 px-2 py-1 text-destructive-foreground line-through">
                <span className="font-mono mr-1">− Step {row.current.step_number}</span>
                {row.current.instruction}
              </div>
            )
          })}
        </div>
      )}

      {/* Final test case — unified-diff view. Walks the LCS sequence so
          unchanged, modified, added, and removed rows render in their
          natural positions, with color-coded styling that mirrors the
          summary diff above. Removed rows are kept inline at their old
          position (struck through, dimmed) so users can see what's
          leaving the test case in context. */}
      <div className="border-t border-subtle pt-2.5">
        <div className="text-[10px] font-medium text-subtle-foreground uppercase tracking-wide mb-1.5">
          Final test case ({proposal.proposed_steps.length} step{proposal.proposed_steps.length === 1 ? '' : 's'})
        </div>
        <ol className="space-y-0.5">
          {unifiedRows.map((row, i) => {
            if (row.kind === 'unchanged') {
              const step = row.proposed
              return (
                <li key={i} className="flex gap-2 px-2 py-0.5">
                  <span className="text-subtle-foreground font-mono w-6 shrink-0 text-right">{step.step_number}.</span>
                  <div className="min-w-0 flex-1">
                    <span className="text-foreground">{step.instruction}</span>
                    {step.expected_result && (
                      <span className="text-subtle-foreground ml-1">→ {step.expected_result}</span>
                    )}
                    {step.step_type !== 'action' && (
                      <span className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium bg-accent-soft text-accent">
                        {step.step_type}
                      </span>
                    )}
                  </div>
                </li>
              )
            }
            if (row.kind === 'modified') {
              return (
                <li key={i} className="flex gap-2 px-2 py-1 rounded bg-warning-soft border border-warning/30">
                  <span className="font-mono w-6 shrink-0 text-right text-warning-foreground">{row.proposed.step_number}.</span>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="text-destructive-foreground line-through">{row.current.instruction}</div>
                    <div className="text-success-foreground">{row.proposed.instruction}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-warning-foreground shrink-0 self-start">modified</span>
                </li>
              )
            }
            if (row.kind === 'added') {
              const step = row.proposed
              return (
                <li key={i} className="flex gap-2 px-2 py-0.5 rounded bg-success-soft border border-success/30 text-success-foreground">
                  <span className="font-mono w-6 shrink-0 text-right">+{step.step_number}.</span>
                  <div className="min-w-0 flex-1">
                    <span>{step.instruction}</span>
                    {step.expected_result && (
                      <span className="opacity-80 ml-1">→ {step.expected_result}</span>
                    )}
                    {step.step_type !== 'action' && (
                      <span className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium bg-accent-soft text-accent">
                        {step.step_type}
                      </span>
                    )}
                  </div>
                </li>
              )
            }
            // removed
            const step = row.current
            return (
              <li key={i} className="flex gap-2 px-2 py-0.5 rounded bg-destructive-soft border border-destructive/30 text-destructive-foreground line-through">
                <span className="font-mono w-6 shrink-0 text-right">−{step.step_number}.</span>
                <div className="min-w-0 flex-1">
                  <span>{step.instruction}</span>
                </div>
              </li>
            )
          })}
        </ol>
        {unchangedCount > 0 && rows.length > 0 && (
          <div className="text-subtle-foreground text-[10px] pt-1.5">
            {unchangedCount} step{unchangedCount === 1 ? '' : 's'} unchanged.
          </div>
        )}
      </div>
    </div>
  )
}

function VariablePreview({ proposal }: { proposal: EnvironmentVariableProposal }) {
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-foreground">{proposal.environment_name}</div>
      <div className="text-xs text-muted-foreground">{proposal.changes_summary}</div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-left text-muted-foreground uppercase tracking-wide">
            <th className="pb-1 pr-3">Key</th>
            <th className="pb-1 pr-3">Change</th>
            <th className="pb-1">Value</th>
          </tr>
        </thead>
        <tbody>
          {proposal.added.map(({ key, value }) => (
            <tr key={key} className="text-success-foreground bg-success-soft">
              <td className="py-0.5 pr-3 font-medium">{key}</td>
              <td className="py-0.5 pr-3"><Plus className="w-3 h-3 inline" /> Added</td>
              <td className="py-0.5 font-mono">{value}</td>
            </tr>
          ))}
          {proposal.updated.map(({ key, old_value, new_value }) => (
            <tr key={key} className="text-warning-foreground bg-warning-soft">
              <td className="py-0.5 pr-3 font-medium">{key}</td>
              <td className="py-0.5 pr-3"><Pencil className="w-3 h-3 inline" /> Updated</td>
              <td className="py-0.5 font-mono"><span className="line-through text-subtle-foreground">{old_value}</span> → {new_value}</td>
            </tr>
          ))}
          {proposal.unchanged.map(({ key }) => (
            <tr key={key} className="text-subtle-foreground">
              <td className="py-0.5 pr-3">{key}</td>
              <td className="py-0.5 pr-3 text-subtle-foreground"><Minus className="w-3 h-3 inline" /> No change</td>
              <td className="py-0.5">—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function WriteProposalCard({
  pendingAction,
  onSave,
  onDiscard,
}: WriteProposalCardProps) {
  const proposal = pendingAction.proposal_data
  if (!proposal) return null

  const proposalType = proposal.type

  return (
    <div className="flex gap-3 justify-start">
      <div className="w-8" /> {/* Spacer to align with assistant messages */}
      <div className="max-w-[85%] rounded-2xl border border-accent/30 bg-accent-soft/50 px-5 py-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-accent" />
          <span className="text-xs font-semibold uppercase tracking-wide text-accent-soft-foreground">
            Preview — Review before saving
          </span>
        </div>

        {/* Dispatch to sub-view based on proposal type */}
        {proposalType === 'create_test_case' && (
          <TestCasePreview proposal={proposal as TestCaseProposal} />
        )}
        {proposalType === 'update_test_case' && (
          <TestCaseUpdatePreview proposal={proposal as TestCaseUpdateProposal} />
        )}
        {proposalType === 'update_test_steps' && (
          <StepDiffPreview proposal={proposal as StepUpdateProposal} />
        )}
        {proposalType === 'update_environment_variables' && (
          <VariablePreview proposal={proposal as EnvironmentVariableProposal} />
        )}

        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-accent/30">
          <button
            onClick={() => onSave(pendingAction.nonce)}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-accent hover:bg-accent/90 rounded-lg transition"
          >
            <Save className="w-3.5 h-3.5" />
            {pendingAction.display?.confirm_label || 'Save'}
          </button>
          <button
            onClick={onDiscard}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-muted-foreground bg-card border border-subtle hover:bg-foreground/[0.04] rounded-lg transition"
          >
            <X className="w-3.5 h-3.5" />
            {pendingAction.display?.cancel_label || 'Discard'}
          </button>
        </div>
      </div>
    </div>
  )
}
